import DateFnsUtils from '@date-io/date-fns';
import { IconButton, InputAdornment, Popover } from '@material-ui/core';
import Icon from '@material-ui/core/Icon';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import CloseIcon from '@material-ui/icons/Close';
import {
  DatePickerProps,
  DateTimePickerProps,
  DatePicker as MuiDatePicker,
  DateTimePicker as MuiDateTimePicker,
  MuiPickersUtilsProvider,
  TimePicker as MuiTimePicker,
  TimePickerProps,
} from '@material-ui/pickers';
import ru from 'date-fns/locale/ru';
import originalMoment from 'moment';
import { extendMoment, default as momentRange } from 'moment-range';
import React, { useEffect, useState } from 'react';
import CoreDateRangePicker from 'react-daterange-picker';
import 'react-daterange-picker/dist/css/react-calendar.css';
import TextField, { ITextFieldProps } from '../TextField';
import './styles.scss';

// @ts-ignore
const moment = extendMoment(originalMoment);

export interface IDatePickerProps extends Omit<DatePickerProps, 'onChange'> {
  onChange: (date: Date) => void;
  onClear?: () => void;
}

const DatePicker: React.FC<IDatePickerProps> = ({ onChange, value, onClear, ...rest }) => {
  const onClickClearButton = (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    if (!!onClear) {
      onClear();
    }
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ru}>
      <MuiDatePicker
        inputVariant="filled"
        okLabel="Выбрать"
        cancelLabel="Отмена"
        InputProps={{
          autoComplete: 'false',
          endAdornment: (
            <InputAdornment position="start">
              <IconButton
                size="small"
                disabled={rest.disabled}
                onClick={onClickClearButton}
                style={{
                  marginRight: 8,
                }}
              >
                <CloseIcon />
              </IconButton>
              <Icon>
                <CalendarTodayIcon />
              </Icon>
            </InputAdornment>
          ),
        }}
        {...rest}
        value={value || null}
        onChange={onChange!}
      />
    </MuiPickersUtilsProvider>
  );
};

export interface IDateTimePickerProps extends Omit<DateTimePickerProps, 'onChange'> {
  onChange: (date: Date) => void;
  onClear?: () => void;
}

export const DateTimePicker: React.FC<IDateTimePickerProps> = ({ onChange, value, onClear, ...rest }) => {
  const onClickClearButton = (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    if (!!onClear) {
      onClear();
    }
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ru}>
      <MuiDateTimePicker
        inputVariant="filled"
        okLabel="Выбрать"
        cancelLabel="Отмена"
        InputProps={{
          autoComplete: 'false',
          endAdornment: (
            <InputAdornment position="start">
              <IconButton
                size="small"
                onClick={onClickClearButton}
                style={{
                  marginRight: 8,
                }}
              >
                <CloseIcon />
              </IconButton>
              <Icon>
                <CalendarTodayIcon />
              </Icon>
            </InputAdornment>
          ),
        }}
        {...rest}
        value={value || null}
        onChange={onChange!}
      />
    </MuiPickersUtilsProvider>
  );
};

export interface ITimePickerProps extends Omit<TimePickerProps, 'onChange'> {
  onChange: (date: Date) => void;
  onClear?: () => void;
}

export const TimePicker: React.FC<ITimePickerProps> = ({ onChange, value, onClear, ...rest }) => {
  const onClickClearButton = (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    if (!!onClear) {
      onClear();
    }
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ru}>
      <MuiTimePicker
        inputVariant="filled"
        okLabel="Выбрать"
        cancelLabel="Отмена"
        InputProps={{
          autoComplete: 'false',
          endAdornment: (
            <InputAdornment position="start">
              <IconButton size="small" onClick={onClickClearButton} style={{ marginRight: 8 }}>
                <CloseIcon />
              </IconButton>

              <Icon>
                <AccessTimeIcon />
              </Icon>
            </InputAdornment>
          ),
        }}
        {...rest}
        value={value || null}
        onChange={onChange!}
      />
    </MuiPickersUtilsProvider>
  );
};

interface IDateRangePickerProps extends Omit<ITextFieldProps, 'onChange'> {
  label: string;
  onClickClear: () => void;
  value: string;
  onChange: (range: string) => void;
  name?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
}

const getRangeFromDate = (stringRange: string) => {
  let dates = stringRange.split('-');
  return moment.range(moment(dates[0], 'DD.MM.YYYY'), moment(dates[1], 'DD.MM.YYYY'));
};

export const DateRangePicker: React.FC<IDateRangePickerProps> = ({ label, onClickClear, ...props }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [value, setValue] = useState<momentRange.DateRange | null>(props.value ? getRangeFromDate(props.value) : null);

  const open = Boolean(anchorEl);
  const id = open ? 'date-range-picker-popover' : undefined;

  const onSelect = (newValue: any) => {
    setAnchorEl(null);
    setValue(newValue);
    props.onChange(`${newValue.start.format('DD.MM.YYYY')}-${newValue.end.format('DD.MM.YYYY')}`);
  };

  const onClearDateRange = () => {
    setValue(null);
    onClickClear();
  };

  useEffect(() => {
    if (props.value) {
      setValue(getRangeFromDate(props.value));
    }
  }, [props.value]);

  const textField = (
    // @ts-ignore
    <TextField
      {...props}
      label={label}
      clear
      fullWidth
      onClick={(event) => setAnchorEl(event.currentTarget)}
      onClickClear={onClearDateRange}
      helperText={props.helperText || ''}
      error={props.error}
      value={props.value}
      InputProps={{ readOnly: true }}
      disabled={props.disabled}
    />
  );

  return (
    <>
      {textField}

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <CoreDateRangePicker
          value={value !== null ? value : moment.range(moment(), moment())}
          onSelect={onSelect}
          singleDateRange
          firstOfWeek={1}
          locale="ru"
        />
      </Popover>
    </>
  );
};

export default DatePicker;
